import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";

// Slugs restricted to vendors and admins. Buyers who hit these URLs are bounced home.
const VENDOR_ONLY_SLUGS = new Set(["vendor-agreement", "vendor-payout-policy"]);

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const inlineFormat = (s) => escapeHtml(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");

// Legal doc content is stored as plain text (with markdown-style ** and # markers,
// but no real HTML), so render it into readable paragraphs/headings instead of
// dumping it through dangerouslySetInnerHTML as-is (which collapses every
// newline into one dense, unformatted block).
function renderLegalHtml(raw) {
  if (!raw) return "";
  const blocks = raw.replace(/\r\n/g, "\n").split(/\n\s*\n/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
      if (headingMatch && !trimmed.includes("\n")) {
        const tag = `h${Math.min(headingMatch[1].length + 1, 6)}`;
        return `<${tag}>${inlineFormat(headingMatch[2])}</${tag}>`;
      }
      const lines = trimmed.split("\n").map((l) => inlineFormat(l.trim()));
      return `<p>${lines.join("<br/>")}</p>`;
    })
    .filter(Boolean)
    .join("\n");
}

function CommissionSummary() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/commission-config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);
  if (!cfg) return null;
  const platform = Number(cfg.platform_fee || 0);
  const threshold = Number(cfg.slab1_threshold || 1000).toLocaleString("en-IN");
  return (
    <div data-testid="payout-slabs-live" className="not-prose mb-8 rounded-2xl border border-[#F48E2E]/30 bg-[#FEF3E6] p-5">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Current rates on file</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Platform fee</div>
          <div className="font-display text-xl">{platform > 0 ? `₹${platform.toLocaleString("en-IN")}` : "None"}</div>
          <div className="text-[11px] text-gray-500">{platform > 0 ? "Charged to the buyer at checkout. Not deducted from vendor payouts." : "No platform fee is charged today."}</div>
        </div>
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="text-xs text-gray-500">GST on commission</div>
          <div className="font-display text-xl">{cfg.gst_on_commission ? `${Number(cfg.gst_pct_on_commission || 18)}%` : "Not applicable"}</div>
          <div className="text-[11px] text-gray-500">{cfg.gst_on_commission ? "Added on top of the commission amount." : "GST is not levied on the commission today."}</div>
        </div>
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Slab 1 · Orders up to ₹{threshold}</div>
          <div className="font-display text-xl">{Number(cfg.slab1_pct)}%</div>
        </div>
        <div className="rounded-xl bg-white border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Slab 2 · Orders above ₹{threshold}</div>
          <div className="font-display text-xl">{Number(cfg.slab2_pct)}%</div>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mt-3">Rates are set by CityShor Admin and may be updated from time to time. This block always reflects the live figures used for weekly payouts.</p>
    </div>
  );
}

export default function LegalDoc({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // Wait for auth to hydrate before enforcing the vendor-only gate.
    if (loading) return;
    // Redirect buyers / unauthenticated visitors away from vendor-only docs.
    if (VENDOR_ONLY_SLUGS.has(slug) && !(user?.role === "vendor" || user?.role === "admin")) {
      nav("/", { replace: true });
      return;
    }
    setDoc(null);
    setErr(null);
    api.get(`/legal/${slug}`).then((r) => setDoc(r.data)).catch(() => setErr("This policy is not available yet."));
  }, [slug, user, loading, nav]);

  if (err) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-gray-500" data-testid="legal-error">{err}</div>;
  if (!doc) return <div className="mx-auto max-w-3xl px-4 py-20 text-center">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12" data-testid={`legal-${slug}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">CityShor Legal</div>
      <h1 className="font-display text-4xl mb-2">{doc.title}</h1>
      {doc.updated_at && (
        <p className="text-xs text-gray-500 mb-8">Last updated {new Date(doc.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
      )}
      {slug === "vendor-payout-policy" && <CommissionSummary />}
      <article
        data-testid="legal-body"
        className="prose prose-neutral max-w-none prose-headings:font-display prose-a:text-[#F48E2E]"
        dangerouslySetInnerHTML={{ __html: renderLegalHtml(doc.body_html) }}
      />
    </div>
  );
}
